import { useEffect, useRef, useState } from "preact/hooks";
import { REVEALED } from "../lib/native.ts";

/**
 * The wordmark's letters, either side of the v (a brand mark, the same in every language), each with its delay in the
 * weight wave: it runs left to right, with the reading, so "r" goes first and "a" last; the v, between, moves at
 * 140ms.
 */
const [LEFT, RIGHT] = [
  [
    ["r", "[--wave-delay:0ms]"],
    ["e", "[--wave-delay:70ms]"],
  ],
  [
    ["i", "[--wave-delay:210ms]"],
    ["s", "[--wave-delay:280ms]"],
    ["a", "[--wave-delay:350ms]"],
  ],
] as const;
/** One wave, first letter to last: the animation (900ms, app.css) plus the last letter's delay. */
const WAVE_MS = 1250;

/**
 * The wordmark: "re", the v, "isa". The v is two pencil strokes (vermilion, Prussian blue) crossing into a tick, drawn to
 * DM Sans' x-height (125%, with overshoot) and stem weight, so it sits in the word at any font size and takes no color
 * from the text. Geometry from the brand files (revisa-brand, the 30° cut used in page headers).
 *
 * A wave of weight (DM Sans is variable) flows through the letters, left to right, when it appears, on hover or a
 * touch, and as a native app shows the sidebar it heads (`REVEALED`); one while one is running lets it finish. Not
 * with reduced motion.
 */
export function Logo({ class: cls = "" }: { class?: string }) {
  // Remounting the letters restarts their animation; until the running wave is over, hovering leaves it be.
  const [wave, setWave] = useState(0);
  const until = useRef(performance.now() + WAVE_MS);
  const replay = () => {
    const now = performance.now();
    if (now >= until.current) {
      until.current = now + WAVE_MS;
      setWave((w) => w + 1);
    }
  };
  // Shown again by a native app (its sidebar's drawer opened, its column back): waving as it comes.
  useEffect(() => {
    addEventListener(REVEALED, replay);
    return () => removeEventListener(REVEALED, replay);
  });
  const letters = (list: typeof LEFT | typeof RIGHT) =>
    list.map(([ch, delay]) => (
      <span key={`${ch}${wave}`} aria-hidden="true" class={`motion-safe:animate-wave ${delay}`}>
        {ch}
      </span>
    ));
  return (
    <span
      role="img"
      aria-label="Revisa"
      class={`inline-flex items-baseline font-sans leading-none font-normal tracking-[-0.01em] whitespace-nowrap ${cls}`}
      onMouseEnter={replay}
      onPointerDown={replay}
    >
      {letters(LEFT)}
      <svg
        key={wave}
        aria-hidden="true"
        viewBox="1.674 4.118 55.652 59.128"
        class="mr-[0.02em] ml-[0.04em] h-[0.65472em] w-[0.61623em] flex-none translate-y-[0.01736em] overflow-visible isolate [--wave-delay:140ms] motion-safe:animate-v-room"
      >
        <V />
      </svg>
      {letters(RIGHT)}
    </span>
  );
}

/**
 * The v: the short red stroke, then the long blue one multiplied over it where they cross. Each stroke is drawn along its
 * own x axis from the v's point, so in the wave rotating leans it about the point (each away from the other: --side,
 * red -1, blue 1), scaling x lengthens it to keep the v's height, and scaling y thickens it about its center line, as
 * the brand's logo study opens the v. The keyframes are generated (scripts/gen-logo-wave.ts).
 */
function V() {
  const Pencil = "[--wave-delay:140ms] origin-[0_0] [transform-box:view-box] motion-safe:animate-pencil";
  return (
    <>
      <g transform="translate(24 58) rotate(-120)" fill="var(--color-vermilion)">
        <g class={`${Pencil} [--side:-1]`}>
          <circle cx="-3.262" cy="0" r="3.838" />
          <polygon points="-3.262,-3.838 28.405,-3.838 38,0 28.405,3.838 -3.262,3.838" />
        </g>
      </g>
      <g
        transform="translate(24 58) rotate(-60)"
        fill="var(--color-prussian)"
        class="[mix-blend-mode:var(--pencil-blend,multiply)]"
      >
        <g class={`${Pencil} [--side:1]`}>
          <circle cx="-3.838" cy="0" r="3.838" />
          <polygon points="-3.838,-3.838 48.869,-3.838 60,0 48.869,3.838 -3.838,3.838" />
        </g>
      </g>
    </>
  );
}
